import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * DocPreview — in-app viewer for admission documents.
 * Handles PDF (via iframe), images inline, everything else → download link.
 */
export default function DocPreview({ doc, onClose }) {
  if (!doc) return null;
  const url = doc.url?.startsWith("http")
    ? doc.url
    : `${process.env.REACT_APP_BACKEND_URL}${doc.url}`;
  const mime = doc.mime || "";
  const fn = (doc.filename || "").toLowerCase();
  const isImg = mime.startsWith("image/") || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(fn);
  const isPdf = mime === "application/pdf" || fn.endsWith(".pdf");
  const label = doc.label || doc.filename || doc.key;

  return (
    <Dialog open={!!doc} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0" data-testid="dialog-doc-preview">
        <DialogHeader className="p-4 border-b flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <FileText size={20} weight="duotone" className="text-primary shrink-0" />
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">{label}</DialogTitle>
              <DialogDescription className="text-xs truncate">
                {doc.filename} {doc.size ? `· ${(doc.size / 1024).toFixed(1)} Ko` : ""} {mime ? `· ${mime}` : ""}
              </DialogDescription>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <a href={url} target="_blank" rel="noreferrer" download={doc.filename}>
              <Button variant="outline" size="sm" data-testid="btn-download-doc">
                <DownloadSimple size={14} className="mr-1" /> Télécharger
              </Button>
            </a>
            <a href={url} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm" data-testid="btn-open-doc">
                <ArrowSquareOut size={14} className="mr-1" /> Ouvrir
              </Button>
            </a>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-muted/40 overflow-auto flex items-center justify-center">
          {isImg && (
            <img src={url} alt={label} className="max-w-full max-h-full object-contain" data-testid="preview-image" />
          )}
          {isPdf && (
            <iframe src={url} title={label} className="w-full h-full border-0" data-testid="preview-pdf" />
          )}
          {!isImg && !isPdf && (
            <div className="p-10 text-center max-w-md">
              <FileText size={48} weight="duotone" className="text-muted-foreground mx-auto" />
              <p className="mt-4 text-sm text-muted-foreground">
                Ce type de fichier ({mime || "inconnu"}) ne peut pas être prévisualisé dans le navigateur.
              </p>
              <a href={url} target="_blank" rel="noreferrer" download={doc.filename}>
                <Button className="mt-4" size="sm" data-testid="btn-download-doc-fallback">
                  <DownloadSimple size={14} className="mr-1" /> Télécharger pour ouvrir
                </Button>
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
